import { useState } from 'react';
import { toast } from 'react-toastify';
import { loadRazorpay } from '../utils/loadRazorpay';
import { proceedToCheckout } from '../services/subscription';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { clearCheckout } from '../store/reducers/checkoutSlice/checkoutSlice';
import { fetchUserInfo } from '../store/action/userInfo';

export const usePayment = () => {
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const initiatePayment = async ({
		billingDetails,
		plan,
		paymentType,
		billingType,
		paymentMethod,
	}) => {
		try {
			setLoading(true);

			const selectedMethod = paymentMethod;
			const isRecurring = paymentType === 'recurring';
			const activeBilling = plan?.billing?.find((item) => item.period === billingType);

			if (!activeBilling) {
				toast.error('Invalid billing plan');
				return;
			}

			const payload = {
				billing: {
					company_name: billingDetails.name,
					phone: billingDetails.phone,
					address_line1: billingDetails.address,
					city: billingDetails.city,
					state: billingDetails.state,
					pincode: billingDetails.pincode,
					country: billingDetails.country,
					gstin_required: billingDetails.gst,
					gstin: billingDetails.gstin || null,
				},
				plan: {
					razorpay_plan_id: activeBilling?.razorpay_plan_id,
					billing_period: activeBilling?.period,
					amount: activeBilling?.amount_in_rupees,
					is_recurring: isRecurring,
					payment_method: selectedMethod,
				},
			};

			const response = await proceedToCheckout(payload);

			if (!response?.success) {
				toast.error(response?.message || 'Unable to proceed checkout');
				return;
			}

			const razorpayLoaded = await loadRazorpay();

			if (!razorpayLoaded) {
				toast.error('Razorpay SDK failed to load');
				return;
			}

			const subscription = response?.data?.subscription;
			const options = {
				key: subscription?.razorpay_key_id,
				subscription_id: subscription?.subscription_id,
				name: 'Pharmnex',
				description: `${subscription?.plan_name} - ${subscription?.billing_period}`,
				currency: subscription?.currency || 'INR',

				prefill: {
					name: billingDetails?.name,
					contact: billingDetails?.phone,
					email: subscription?.customer?.email || '',
				},

				method: {
					upi: selectedMethod === 'upi',
					card: selectedMethod === 'card',
					netbanking: false,
					wallet: false,
					emi: false,
					paylater: false,
				},

				config: {
					display: {
						blocks: {
							upi: { name: 'Pay Using UPI', instruments: [{ method: 'upi' }] },
							card: { name: 'Pay Using Card', instruments: [{ method: 'card' }] },
						},
						sequence: selectedMethod === 'upi' ? ['block.upi'] : ['block.card'],
						preferences: { show_default_blocks: false },
					},
				},

				...(isRecurring && { recurring: 1 }),

				theme: { color: '#3B58FF' },

				handler: async (paymentResponse) => {
					dispatch(clearCheckout());
					// openSubscriptionCheckout's success path does a full page
					// reload, which incidentally refreshes the app's Redux
					// userInfo/subscription state. This path navigates via
					// React Router instead (no reload), so without this the
					// rest of the app (sidebar, plan gates, etc.) would keep
					// showing pre-payment subscription state until the user
					// manually refreshed. Await it before navigating so
					// /payment-success and everything after it sees the new plan.
					try {
						await dispatch(fetchUserInfo());
					} catch (err) {
						console.error('Failed to refresh user info after payment:', err);
					}
					navigate('/payment-success', { replace: true });
				},

				modal: {
					ondismiss: () => {
						toast.error('Payment cancelled');
					},
				},
			};

			const razorpay = new window.Razorpay(options);

			razorpay.on('payment.failed', (response) => {
				toast.error(response?.error?.description || 'Payment failed');
			});

			razorpay.open();
		} catch (error) {
			console.error(error);
			toast.error(error?.response?.data?.message || 'Payment initialization failed');
		} finally {
			setLoading(false);
		}
	};

	const openSubscriptionCheckout = async ({
		subscription,
		billingDetails,
		paymentMethod = 'card',
	}) => {
		try {
			setLoading(true);

			const razorpayLoaded = await loadRazorpay();

			if (!razorpayLoaded) {
				toast.error('Razorpay SDK failed to load');
				return;
			}

			const options = {
				key: subscription.razorpay_key_id,
				subscription_id: subscription.subscription_id,
				name: 'Pharmnex',
				description: `${subscription.plan_name} - ${subscription.billing_period}`,
				currency: subscription.currency || 'INR',

				prefill: {
					name: billingDetails?.company_name,
					contact: billingDetails?.phone,
					email: subscription.customer?.email,
				},

				method: {
					upi: paymentMethod === 'upi',
					card: paymentMethod === 'card',
				},

				recurring: 1,

				theme: {
					color: '#3B58FF',
				},

				handler: () => {
					window.location.reload();
				},
			};

			new window.Razorpay(options).open();
		} catch (error) {
			console.error('Failed to open Razorpay subscription checkout:', error);
			toast.error('Payment window failed to open. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	return {
		initiatePayment,
		openSubscriptionCheckout,
		loading,
	};
};
